import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import useGetTasks from "api/queries/user/useGetTasks";
import { useFormatPreferredDatetime } from "hooks/useFormatPreferredDatetime";
import Button from "components/v2/Button/Button";
import Card from "components/v2/Card/Card";
import Badge from "components/v2/Badge/Badge";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { ReactComponent as DotPoints } from "assets/images/iconsV2/dotpoints.svg";
import { ReactComponent as ClipboardCheck } from "assets/images/iconsV2/clipboard-check-grey.svg";
import IconCircle from "components/v2/IconCircle/IconCircle";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import TaskDialog from "../../../../../../shipper/Tasks/components/TaskDialog";

import "./TasksWidget.scss";

function TasksWidget() {
  const intl = useIntl();
  const navigate = useNavigate();

  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskId, setTaskId] = useState<number>();

  const { data: tasksData, isFetching } = useGetTasks();
  const { formatPreferredDatetime } = useFormatPreferredDatetime("D MMM, HH:mm");

  const handleViewAll = () => {
    navigate("/tasks?status=open&page=1");
  };

  const handleTaskClick = (id: number, type: string, shipment_id: string) => {
    switch (type) {
      case "cost_recovery":
        setTaskId(id);
        setShowTaskDialog(true);
        break;
      case "cancel_request":
      case "pending_approval_request":
        navigate(`/shipment/${shipment_id}`);
        break;
      default:
        navigate(`/shipment/${shipment_id}/tracking`);
        break;
    }
  };

  return (
    <>
      {taskId && (
        <TaskDialog
          isOpen={showTaskDialog}
          close={() => {
            setShowTaskDialog(false);
          }}
          taskId={taskId}
        />
      )}

      <Card borderRadius="md" shadow="xs">
        <div className="tasks-widget">
          <div className="tasks-widget__header">
            <div className="tasks-widget__icon">
              <DotPoints />
            </div>
            <div className="w-100 d-flex align-items-center">
              <div className="font-medium text-md text-primary mr-2">
                <FormattedMessage id="general.tasks" defaultMessage="Tasks" />
              </div>
              <div>{!isFetching && <Badge size="sm" color="gray" type="category-badge" label={tasksData?.count} />}</div>
            </div>
            <Button
              className="no-wrap"
              variant="link-color"
              label={intl.formatMessage({ id: "general.viewAll", defaultMessage: "View all" })}
              onClick={handleViewAll}
            />
          </div>

          {/* Loading State */}
          {isFetching && (
            <div className="tasks-widget__loading">
              <LoadingSkeleton rows={2} />
            </div>
          )}

          {/* Empty State */}
          {tasksData?.results.length === 0 && !isFetching && (
            <div className="tasks-widget__empty-state">
              <EmptyState
                noActions
                customIcon={<IconCircle icon={ClipboardCheck} color="tertiary" size="lg" />}
                title={intl.formatMessage({
                  id: "dashboard.widgets.tasks.emptyTitle",
                  defaultMessage: "No new tasks",
                })}
                description={intl.formatMessage({
                  id: "dashboard.widgets.tasks.emptyDescription",
                  defaultMessage: "When you have new tasks, they will show up here.",
                })}
              />
            </div>
          )}

          {/* Task list */}
          {tasksData?.results.length > 0 && !isFetching && (
            <div className="tasks-widget__content">
              {tasksData?.results.slice(0, 5).map(({ created_at, shipment_id, name, id, type }: any) => (
                <div
                  className="tasks-widget__item cursor-pointer bg-primary-hover"
                  key={id}
                  onClick={() => handleTaskClick(id, type, shipment_id)}
                >
                  <div className="d-flex justify-content-between w-100 gap-sm">
                    <div className="d-flex align-items-center">
                      <div className="text-sm font-medium text-primary">{shipment_id}</div>
                    </div>
                    <div className="text-xs text-tertiary flex-none">{formatPreferredDatetime(created_at)}</div>
                  </div>
                  <div className="tasks-widget__description text-sm text-secondary">{name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </>
  );
}

export default TasksWidget;
